/**
 * Hook for fetching specimens (frozen eggs/embryos)
 */

import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody } from "@src/packages/openapi/helpers";
import {
  buildNotesString,
  buildFrozenStorageLocation,
  buildEmbryoBiopsy,
  parseGrade,
  type Cycle,
  type SpecimenSource,
} from "@src/packages/care/components/frozen-storage-data";
import { throwApiError } from "@src/packages/api/api-error";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import { logClinicalMutationError } from "../clinical-log-redaction";
import { isInFrozenStorage } from "./frozenSpecimenAvailability";
import { resolveSemenSampleIds } from "./specimen-sample-identity";
import {
  placementForRow,
  surplusClosures,
  usageForRow,
} from "./specimenIntakeUpdate";
import {
  semenSampleIdentity,
  specimenItemIdentity,
} from "./specimenRetryIdentity";
export { getRemainingOocyteCohortCount } from "./oocyteCohortInventory";
export { createIdempotencyKey } from "@src/packages/api/idempotency";

export type SpecimenType = "semen" | "oocyte" | "embryo";
export type SpecimenStatus =
  | "collected"
  | "processed"
  | "stored"
  | "used"
  | "discarded"
  | "donated"
  | "in_transit";

type ApiPatientSpecimen = NonNullable<
  paths["/{tenantId}/patients/{patientId}/specimens"]["get"]["responses"][200]["content"]["application/json"]["data"]
>[number];
type ApiSemenSpecimen = Extract<ApiPatientSpecimen, { type: "semen" }>;
type ApiOocyteSpecimen = Extract<ApiPatientSpecimen, { type: "oocyte" }>;
type ApiEmbryoSpecimen = Extract<ApiPatientSpecimen, { type: "embryo" }>;
type ApiStorageLocation = ApiPatientSpecimen["storageLocations"][number];
type ApiSpecimenUsage = ApiPatientSpecimen["specimenUsages"][number];
type ApiThawingInfo = ApiSpecimenUsage["thawingInfo"];

/**
 * Patient-facing preparation choices are richer than the legacy specimen
 * processing state. Store the former in the spermiogram and keep the latter
 * as `frozen` for specimens created by this intake.
 */
const SEMEN_PREPARATION_METHODS: Record<string, string | null> = {
  none: null,
  washed: "simple_wash",
  swim_up: "swim_up",
  density_gradient: "dgc",
  dgc_swim_up: "dgc_swim_up",
  microfluidics: "microfluidics",
  macs: "macs",
  other: null,
};

/**
 * The API's collection-method vocabulary, and what to do with the one answer it
 * has no word for.
 *
 * `SEMEN_COLLECTION_METHODS` offers "Other" and calls itself "supported by the
 * API", which it is not: the enum is ejaculation/tese/mesa/pesa/tesa/mtese/
 * electroejaculation/post_ejaculatory_urine. Sending `other` was rejected and,
 * because the batch validates `.strict()`, took the whole save with it.
 *
 * Mapped to `null` the way `SEMEN_PREPARATION_METHODS` already maps its own
 * `other` — "she answered, and the answer is not one the record can hold" is an
 * absence, not a value. That absence needs the column nullable, which is
 * `dashboard-api#fix/semen-collection-method-optional`; until it lands, "Other"
 * fails the same way an unanswered field does, rather than differently.
 *
 * Unknown keys map to `null` too. A UI list that gains an option the enum does
 * not have must not be able to break the save again.
 */
const SEMEN_COLLECTION_METHODS_TO_API: Record<
  string,
  // Typed from the API rather than as a bare string, so the map stops compiling
  // if the enum moves — which is the whole point of deriving types from the
  // schema. `null` is what "no enum member exists for this answer" means; the
  // call site omits the key rather than sending it.
  NonNullable<CreateSemenBatchItem["data"]["collectionMethod"]> | null
> = {
  ejaculation: "ejaculation",
  tese: "tese",
  tesa: "tesa",
  mesa: "mesa",
  pesa: "pesa",
  mtese: "mtese",
  electroejaculation: "electroejaculation",
  post_ejaculatory_urine: "post_ejaculatory_urine",
  other: null,
};

type RequiredStorageLocationFields =
  "storedFrom" | "storedTo" | "isActive" | "facility" | "storageType" | "notes";

export type StorageLocation = Required<
  Pick<ApiStorageLocation, RequiredStorageLocationFields>
> &
  Partial<Omit<ApiStorageLocation, RequiredStorageLocationFields>>;

export type StorageLocationPatch = NonNullable<
  RequestBody<
    paths["/{tenantId}/specimens/oocyte/{id}"]["patch"]
  >["storageLocations"]
>[number];

export type SpecimenUsage = Partial<ApiSpecimenUsage> & {
  usage: ApiSpecimenUsage["usage"] | "reserve";
  hasDecisionAndConsent: ApiSpecimenUsage["hasDecisionAndConsent"];
  // The patient route returns cycle linkage before it is represented in the
  // checked-in generated schema.
  cycleId?: string | null;
};

/** Exact material-ledger command for a specimen lifecycle transition. */
export type SpecimenMaterialOperation = RequestBody<
  paths["/{tenantId}/specimens/{id}/material-operations"]["post"]
>;
export type SpecimenMaterialOperationAction =
  SpecimenMaterialOperation["action"];

/**
 * Patient specimen responses are a discriminated union in the generated
 * schema. Keep that contract intact and only retain the legacy top-level
 * thawingInfo projection used by the fertility estimate widget.
 */
export type Specimen = Omit<
  ApiPatientSpecimen,
  "storageLocations" | "specimenUsages"
> & {
  // These optional projections keep existing consumers type-safe while the
  // generated response remains a discriminated union by specimen type.
  isDonor?:
    | ApiSemenSpecimen["isDonor"]
    | ApiOocyteSpecimen["isDonor"]
    | ApiEmbryoSpecimen["isDonor"];
  sampleId?: ApiSemenSpecimen["sampleId"];
  origin?: ApiSemenSpecimen["origin"];
  processingMethod?: ApiSemenSpecimen["processingMethod"];
  isProcessed?: ApiSemenSpecimen["isProcessed"];
  freezeThawCycles?: ApiSemenSpecimen["freezeThawCycles"];
  spermiogram?: ApiSemenSpecimen["spermiogram"];
  /** Specimen-level semen metrics; the API codes them as observations. */
  vitality?: ApiSemenSpecimen["vitality"];
  dnaFragmentation?: ApiSemenSpecimen["dnaFragmentation"];
  storageLocations: StorageLocation[];
  specimenUsages: SpecimenUsage[];
  thawingInfo?: ApiThawingInfo;
  maturityStage?: ApiOocyteSpecimen["maturityStage"];
  inseminationGroupId?: ApiOocyteSpecimen["inseminationGroupId"];
  oocyteSpecimenId?: ApiEmbryoSpecimen["oocyteSpecimenId"];
  dayDetails?: ApiEmbryoSpecimen["dayDetails"];
  aiScores?: ApiEmbryoSpecimen["aiScores"];
};

type OocyteCohortResponse = NonNullable<
  paths["/patients/{patientId}/oocyte-cohorts"]["get"]["responses"][200]["content"]["application/json"]["data"]
>;
export type OocyteCohort = OocyteCohortResponse[number];
export type OocyteCohortMember = OocyteCohort["members"][number];
export type OocyteCohortStatus = OocyteCohort["status"];
export type OocyteCohortInput = RequestBody<
  paths["/patients/{patientId}/oocyte-cohorts"]["post"]
>;
export type OocyteCohortPatch = RequestBody<
  paths["/patients/{patientId}/oocyte-cohorts/{id}"]["patch"]
>;
type OocyteCohortCountField =
  | "totalRetrieved"
  | "matureMII"
  | "immatureMI"
  | "degenerated"
  | "numberFrozen"
  | "numberThawed"
  | "fertilizedCount";
export type OocyteCohortUpdateInput = Omit<
  OocyteCohortPatch,
  OocyteCohortCountField
>;

type OocyteSpecimenPatch = NonNullable<
  paths["/{tenantId}/specimens/oocyte/{id}"]["patch"]["requestBody"]
>["content"]["application/json"];
type SemenSpecimenPatch = NonNullable<
  paths["/{tenantId}/specimens/semen/{id}"]["patch"]["requestBody"]
>["content"]["application/json"];
type EmbryoSpecimenPatch = NonNullable<
  paths["/{tenantId}/specimens/embryo/{id}"]["patch"]["requestBody"]
>["content"]["application/json"];

export type SpecimenPatchInput =
  | { id: string; type: "oocyte"; data: OocyteSpecimenPatch }
  | { id: string; type: "semen"; data: SemenSpecimenPatch }
  | { id: string; type: "embryo"; data: EmbryoSpecimenPatch };

/**
 * Fetch all specimens for the current patient
 */
export function useSpecimens(
  type?: SpecimenType,
  options?: { enabled?: boolean },
) {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<Specimen[]>({
    queryKey: ["specimens", tenantId, patientId, type],
    queryFn: async () => {
      type SpecimensApiClient = {
        GET(
          path: string,
          options: object,
        ): Promise<{ data?: { data: Specimen[] } | null; error?: unknown }>;
      };
      const { data, error } = await (
        client as unknown as SpecimensApiClient
      ).GET("/{tenantId}/patients/{patientId}/specimens", {
        params: {
          path: {
            tenantId,
            patientId,
          },
          query: type ? { type } : undefined,
        },
      });

      if (data) {
        return data.data ?? [];
      }
      if (error) {
        throwApiError(error, "Failed to load specimens", "specimens");
      }
      throwApiError(undefined, "Failed to load specimens", "specimens");
    },
    enabled: !!patientId && !!tenantId && options?.enabled !== false,
  });
}

/** Fetch explicit oocyte cohorts without inferring them from individual rows. */
export function useOocyteCohorts() {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<OocyteCohort[]>({
    queryKey: ["oocyte-cohorts", tenantId, patientId],
    queryFn: async () => {
      const { data, error } = await client.GET(
        "/patients/{patientId}/oocyte-cohorts",
        {
          params: {
            path: {
              patientId,
            },
          },
        },
      );

      if (data) return data.data ?? [];
      if (error) {
        throwApiError(error, "Failed to load egg cohorts", "oocyte-cohorts");
      }
      throwApiError(undefined, "Failed to load egg cohorts", "oocyte-cohorts");
    },
    enabled: !!patientId && !!tenantId,
  });
}

/**
 * Update a specimen and any nested storage, usage, biopsy, or day-detail
 * records included in the generated request body. Keeping this in one hook
 * prevents each modal from reimplementing the type-specific legacy routes.
 */
export function useUpdateSpecimen() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SpecimenPatchInput) => {
      const result =
        input.type === "oocyte"
          ? await client.PATCH("/{tenantId}/specimens/oocyte/{id}", {
              params: { path: { tenantId, id: input.id } },
              body: input.data,
            })
          : input.type === "semen"
            ? await client.PATCH("/{tenantId}/specimens/semen/{id}", {
                params: { path: { tenantId, id: input.id } },
                body: input.data,
              })
            : await client.PATCH("/{tenantId}/specimens/embryo/{id}", {
                params: { path: { tenantId, id: input.id } },
                body: input.data,
              });

      if (result.error) {
        throwApiError(result.error, "Failed to update specimen", "specimens");
      }
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["specimens"] });
      await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
    },
  });
}

/** Record a lifecycle transition with exact material quantity and unit. */
export function useRecordSpecimenMaterialOperation() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      operation: SpecimenMaterialOperation;
    }) => {
      const result = await client.POST(
        "/{tenantId}/specimens/{id}/material-operations",
        {
          params: {
            path: { tenantId, id: input.id },
          },
          body: input.operation,
        },
      );

      if (result.error) {
        throwApiError(
          result.error,
          `Failed to record ${input.operation.action} on ${input.id}`,
          "specimens",
          result.response?.status,
        );
      }
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["specimens"] });
      await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
    },
  });
}

/**
 * Write a `thaw` material operation on every oocyte picked for the cycle.
 * The fertilization count is not written per-specimen: the cycle's
 * `fertilization` phase already carries it as a `fertilized_oocytes` metric
 * (the authoritative source the lab funnel already reads). Deriving it there
 * avoids the specimen `use`/`fertilize` write path, which the deprecated
 * material-operations endpoint rejects for oocytes without also naming a
 * sperm specimen and an embryo output — data the treatment-hub step never
 * collects. Idempotent per specimen: a `thaw` already present for this cycle
 * is skipped.
 */
export function useRecordCycleThaw() {
  const recordMaterialOperation = useRecordSpecimenMaterialOperation();
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();

  return useMutation({
    mutationFn: async (input: {
      cycleId: string;
      /**
       * The oocyte specimen ids to mark thawed, from the cycle's
       * `contextSnapshot.materialIds` (the eggs picked when it started).
       */
      specimenIds: readonly string[];
      occurredAt?: string;
      hasDecisionAndConsent: boolean;
    }) => {
      const cachedSpecimens = queryClient
        .getQueriesData<Specimen[]>({
          queryKey: ["specimens", tenantId, patientId],
        })
        .flatMap(([, data]) => data ?? []);
      const cachedById = new Map(cachedSpecimens.map((s) => [s.id, s]));
      const occurredAt = input.occurredAt ?? new Date().toISOString();
      const writes = input.specimenIds.map(async (id) => {
        const cached = cachedById.get(id);
        const alreadyThawed = cached?.specimenUsages.some(
          (usage) => usage.cycleId === input.cycleId && usage.usage === "thaw",
        );
        if (alreadyThawed) return;
        await recordMaterialOperation.mutateAsync({
          id,
          operation: {
            clientRequestId: createIdempotencyKey(),
            action: "thaw",
            occurredAt,
            quantity: "1",
            unit: "count",
            cycleId: input.cycleId,
            hasDecisionAndConsent: input.hasDecisionAndConsent,
          },
        });
      });
      const results = await Promise.allSettled(writes);
      const failures = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      if (failures.length > 0) {
        for (const failure of failures) {
          logClinicalMutationError(
            "[useRecordCycleThaw]",
            "/{tenantId}/specimens/{id}/material-operations",
            failure.reason,
          );
        }
      }
      return {
        writes: writes.length,
        failed: failures.length,
      };
    },
  });
}

/** Record confirmed embryo use for the exact embryos transferred in an FET. */
export function useRecordCycleUse() {
  const recordMaterialOperation = useRecordSpecimenMaterialOperation();
  const queryClient = useQueryClient();
  const { tenantId, patientId } = useApiClient();
  const requestIds = useRef(new Map<string, string>());

  return useMutation({
    mutationFn: async (input: {
      cycleId: string;
      specimenIds: readonly string[];
      occurredAt?: string;
      hasDecisionAndConsent: boolean;
    }) => {
      const cachedSpecimens = queryClient
        .getQueriesData<Specimen[]>({
          queryKey: ["specimens", tenantId, patientId],
        })
        .flatMap(([, data]) => data ?? []);
      const cachedById = new Map(cachedSpecimens.map((s) => [s.id, s]));
      const occurredAt = input.occurredAt ?? new Date().toISOString();
      const writes = input.specimenIds.map(async (id) => {
        const cached = cachedById.get(id);
        const alreadyUsed =
          cached?.status === "used" ||
          cached?.specimenUsages.some(
            (usage) => usage.cycleId === input.cycleId && usage.usage === "use",
          );
        if (alreadyUsed) return;
        const requestKey = `${input.cycleId}:${id}`;
        const clientRequestId =
          requestIds.current.get(requestKey) ?? createIdempotencyKey();
        requestIds.current.set(requestKey, clientRequestId);
        await recordMaterialOperation.mutateAsync({
          id,
          operation: {
            clientRequestId,
            action: "use",
            occurredAt,
            quantity: "1",
            unit: "count",
            cycleId: input.cycleId,
            hasDecisionAndConsent: input.hasDecisionAndConsent,
          },
        });
      });
      const results = await Promise.allSettled(writes);
      const failures = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );
      if (failures.length > 0) {
        for (const failure of failures) {
          logClinicalMutationError(
            "[useRecordCycleUse]",
            "/{tenantId}/specimens/{id}/material-operations",
            failure.reason,
          );
        }
      }
      return {
        writes: writes.length,
        failed: failures.length,
      };
    },
  });
}

/**
 * Delete a specimen record. This is intentionally exposed as a data-layer
 * mutation only; user-facing discard/donate actions need separate lifecycle
 * semantics and should not silently delete clinical history.
 */
export function useDeleteSpecimen() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (specimenId: string) => {
      if (!specimenId) throw new Error("specimenId is required");
      const result = await client.DELETE("/{tenantId}/specimens/{id}", {
        params: { path: { tenantId, id: specimenId } },
      });
      if (result.error) {
        throwApiError(result.error, "Failed to delete specimen", "specimens");
      }
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["specimens"] });
      await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
    },
  });
}

/** Update cohort metadata and explicit specimen membership. */
export function useUpdateOocyteCohort() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cohortId,
      data,
    }: {
      cohortId: string;
      data: OocyteCohortUpdateInput;
    }) => {
      const result = await client.PATCH(
        "/patients/{patientId}/oocyte-cohorts/{id}",
        {
          params: { path: { patientId, id: cohortId } },
          body: data,
        },
      );
      if (result.error) {
        throwApiError(
          result.error,
          "Failed to update egg cohort",
          "oocyte-cohorts",
        );
      }
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
      await queryClient.invalidateQueries({ queryKey: ["specimens"] });
    },
  });
}

/**
 * Get frozen specimens (eggs and embryos with active storage)
 */
export function useFrozenSpecimens() {
  const {
    data: specimens,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useSpecimens();

  // Filter to eggs, embryos, and sperm that are currently stored
  const frozenSpecimens = (specimens ?? []).filter(
    (specimen) =>
      (specimen.type === "oocyte" ||
        specimen.type === "embryo" ||
        specimen.type === "semen") &&
      // One rule, shared with the homepage's inventory snapshot. Written out
      // twice, the two drifted and the same rows counted on one surface and
      // not the other.
      isInFrozenStorage(specimen),
  );

  // Do not merge rows on type/facility alone. The legacy specimen response
  // does not expose a cohort key, so keep each stable specimen identity
  // separate until the server supplies an explicit batch relationship.
  const groupedByFacility = frozenSpecimens.reduce(
    (acc, specimen) => {
      const activeStorage = specimen.storageLocations.find(
        (loc) => loc.isActive && loc.storageType === "frozen",
      );
      if (!activeStorage) return acc;

      const key = `${specimen.type}-${activeStorage.facility}-${specimen.id}`;
      const group =
        acc[key] ??
        (acc[key] = {
          type: specimen.type as "oocyte" | "embryo" | "semen",
          facility: activeStorage.facility ?? "",
          specimens: [],
          storedDate: activeStorage.storedFrom,
          notes: [],
          collectedAt: specimen.collectedAt,
          specimenIds: [],
          storageLocationNotes: activeStorage.notes ?? null,
        });
      group.specimens.push(specimen);
      if (specimen.notes) group.notes.push(specimen.notes);
      group.specimenIds.push(specimen.id);
      // Use earliest stored date
      if (activeStorage.storedFrom < group.storedDate) {
        group.storedDate = activeStorage.storedFrom;
      }
      // Use earliest collectedAt
      if (
        specimen.collectedAt &&
        (!group.collectedAt || specimen.collectedAt < group.collectedAt)
      ) {
        group.collectedAt = specimen.collectedAt;
      }
      return acc;
    },
    {} as Record<
      string,
      {
        type: "oocyte" | "embryo" | "semen";
        facility: string;
        specimens: Specimen[];
        storedDate: string;
        notes: string[];
        collectedAt: string | null;
        specimenIds: string[];
        storageLocationNotes: string | null;
      }
    >,
  );

  // Convert to array for display
  const storageGroups = Object.values(groupedByFacility);

  // Calculate totals
  const totalEggs = frozenSpecimens.filter((s) => s.type === "oocyte").length;
  const totalEmbryos = frozenSpecimens.filter(
    (s) => s.type === "embryo",
  ).length;
  const totalSperm = frozenSpecimens.filter((s) => s.type === "semen").length;

  return {
    specimens: frozenSpecimens,
    storageGroups,
    totalEggs,
    totalEmbryos,
    totalSperm,
    isLoading,
    error,
    isFetching,
    refetch,
  };
}

/**
 * Cycle-scoped variant of useFrozenSpecimens.
 * Filters to only specimens linked to the given cycleId.
 */
export function useFrozenSpecimensForCycle(cycleId: string | null | undefined) {
  const all = useFrozenSpecimens();

  const cycleSpecimens = cycleId
    ? all.specimens.filter((s) => s.cycleId === cycleId)
    : [];

  const cycleTotalEggs = cycleSpecimens.filter(
    (s) => s.type === "oocyte",
  ).length;
  const cycleTotalEmbryos = cycleSpecimens.filter(
    (s) => s.type === "embryo",
  ).length;
  const cycleTotalSperm = cycleSpecimens.filter(
    (s) => s.type === "semen",
  ).length;

  // Group cycle specimens by type + facility
  const grouped: Record<
    string,
    {
      type: "oocyte" | "embryo" | "semen";
      facility: string;
      specimens: Specimen[];
      storedDate: string;
      notes: string[];
      collectedAt: string | null;
      specimenIds: string[];
      storageLocationNotes: string | null;
    }
  > = {};
  for (const specimen of cycleSpecimens) {
    const activeStorage = specimen.storageLocations.find(
      (loc) => loc.isActive && loc.storageType === "frozen",
    );
    if (!activeStorage) continue;
    const key = `${specimen.type}-${activeStorage.facility}`;
    const group =
      grouped[key] ??
      (grouped[key] = {
        type: specimen.type as "oocyte" | "embryo" | "semen",
        facility: activeStorage.facility ?? "",
        specimens: [],
        storedDate: activeStorage.storedFrom,
        notes: [],
        collectedAt: specimen.collectedAt,
        specimenIds: [],
        storageLocationNotes: activeStorage.notes ?? null,
      });
    group.specimens.push(specimen);
    group.specimenIds.push(specimen.id);
    if (specimen.notes) group.notes.push(specimen.notes);
    if (
      specimen.collectedAt &&
      (!group.collectedAt || specimen.collectedAt < group.collectedAt)
    ) {
      group.collectedAt = specimen.collectedAt;
    }
    if (activeStorage.storedFrom < group.storedDate) {
      group.storedDate = activeStorage.storedFrom;
    }
  }
  const cycleStorageGroups = Object.values(grouped);

  return {
    ...all,
    cycleSpecimens,
    cycleTotalEggs,
    cycleTotalEmbryos,
    cycleTotalSperm,
    cycleStorageGroups,
  };
}

type SpecimenBatchRequest = RequestBody<
  paths["/patients/{patientId}/specimens/batch"]["post"]
>;
type SpecimenBatchResponse = NonNullable<
  paths["/patients/{patientId}/specimens/batch"]["post"]["responses"][200]["content"]["application/json"]["data"]
>;
export type SpecimenBatchResult = SpecimenBatchResponse & {
  specimenIdsByCycle: Record<number, string[]>;
  /**
   * Rows this save took back, by cycle index.
   *
   * The caller has to drop them from the cycle's identity: left there, a second
   * save in the same session re-issues `op: "delete"` against an episode that
   * is already gone.
   */
  retiredSpecimenIdsByCycle: Record<number, string[]>;
};
type SpecimenBatchItem = SpecimenBatchRequest["items"][number];
type CreateSemenBatchItem = Extract<
  SpecimenBatchItem,
  { operation: "create"; type: "semen" }
>;
type CreateOocyteBatchItem = Extract<
  SpecimenBatchItem,
  { operation: "create"; type: "oocyte" }
>;
type CreateEmbryoBatchItem = Extract<
  SpecimenBatchItem,
  { operation: "create"; type: "embryo" }
>;
type UpdateSemenBatchItem = Extract<
  SpecimenBatchItem,
  { operation: "update"; type: "semen" }
>;
type UpdateOocyteBatchItem = Extract<
  SpecimenBatchItem,
  { operation: "update"; type: "oocyte" }
>;
type UpdateEmbryoBatchItem = Extract<
  SpecimenBatchItem,
  { operation: "update"; type: "embryo" }
>;
/**
 * A specimen batch can commit some rows before another row fails. Keep the
 * original cycles and keys so the next attempt replays the same operation;
 * the API skips items it already committed and retries only the failures.
 */
/**
 * What an intake record can honestly say about consent.
 *
 * A patient logging biology that was frozen at a clinic is recording history,
 * not authorizing anything, so the answer is always "no decision and consent
 * recorded" — never true. Named rather than inlined so the four freeze usages
 * cannot drift apart, and so the value reads as a statement instead of a magic
 * `false`.
 */
const SPECIMEN_INTAKE_HAS_CONSENT = false;

export class SpecimenBatchError extends Error {
  constructor(
    readonly retryCycles: Cycle[],
    readonly succeededCount: number,
    readonly failedCount: number,
    readonly retryIdempotencyKeys: Record<string, string>,
    readonly retrySampleIds: Record<string, string>,
    readonly operationKey: string,
  ) {
    super(`Failed to save ${failedCount} specimen(s)`);
    this.name = "SpecimenBatchError";
  }
}

type SpecimenRequest = {
  cycleIndex: number;
  specimenIndex: number;
  /** Stable retry identity (#2431) — keys the retryIdempotencyKeys map. */
  identity: string;
  existingId?: string;
  /** True for a row this save takes back rather than writes. */
  retires?: boolean;
  idempotencyKey: string;
  batchItem: SpecimenBatchItem;
};

type CreateSpecimenInput = {
  cycles: Cycle[];
  cycleId?: string;
  idempotencyKeys?: Record<string, string>;
  /** Stable sample identity by cycle index for resumable semen batches. */
  sampleIds?: Record<string, string>;
  operationKey?: string;
};

function donorFlag(source: SpecimenSource | null): boolean | null {
  if (source === "donor") return true;
  if (source === "mine" || source === "partner") return false;
  return null;
}

/**
 * Create new specimens (oocytes or embryos) from cycle objects.
 * Accepts complete Cycle objects from frozen-storage-data.
 */
/** A share she typed as a percentage, when it can be sent as one. */
function isPercent(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value >= 0 && value <= 100;
}

export function useCreateSpecimen() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSpecimenInput) => {
      const requests: SpecimenRequest[] = [];
      const operationKey = input.operationKey ?? createIdempotencyKey();
      const sampleIds = resolveSemenSampleIds(
        input.cycles,
        input.sampleIds,
        createIdempotencyKey,
      );
      // Stable retry identities (#2431): keys survive removing/reordering
      // sibling cycles and individual embryo rows between retries, so a new
      // row can never inherit a committed row's client-item key (which the
      // server's per-item idempotency would silently drop).
      const getIdempotencyKey = (identity: string) =>
        input.idempotencyKeys?.[identity] ?? createIdempotencyKey();

      for (const [cycleIndex, cycle] of input.cycles.entries()) {
        /*
         * Only rows that were read for THIS type may be updated.
         *
         * `handleManualNext` and `handleAddResultingEmbryos` both change a
         * cycle's type after it is built and keep its ids, so without this an
         * egg intake turned into an embryo one would send
         * `{operation: "update", type: "embryo", specimenId: <an oocyte>}` and
         * `closeSurplus` would retire her oocyte rows. The stamp travels with
         * the ids from wherever they were seeded, so it covers the in-session
         * save as well as the prefill.
         */
        const existingIds =
          cycle.specimenIdsType && cycle.specimenIdsType !== cycle.type
            ? []
            : (cycle.specimenIds ?? []);

        const dateString =
          cycle.type === "eggs"
            ? cycle.oocyte.freezeDate || cycle.oocyte.retrievalDate
            : cycle.type === "sperm"
              ? cycle.sperm.freezeDate || cycle.sperm.collectedAt
              : cycle.embryo.freezeDate || cycle.embryo.fertilisationDate;
        const collectedAt =
          cycle.type === "eggs" && cycle.oocyte.retrievalDate
            ? cycle.oocyte.retrievalDate
            : cycle.type === "sperm" && cycle.sperm.collectedAt
              ? cycle.sperm.collectedAt
              : cycle.type === "embryos" && cycle.embryo.fertilisationDate
                ? cycle.embryo.fertilisationDate
                : dateString;

        const notesStr = buildNotesString(cycle);
        const facility = cycle.clinicName.trim();
        const baseStorage = buildFrozenStorageLocation(
          cycle,
          dateString,
          facility,
        );
        /**
         * An explicit stored state plus a date creates the placement. Where it
         * is stored does not gate it.
         *
         * A patient can know her eggs are frozen and not be sure exactly where
         * they are, and requiring the facility made that case unrecordable:
         * without a name no episode was created, the specimen was written as
         * `processed`, and the vault — which reads only `stored` rows with an
         * active episode — showed her nothing at all. No message, no error, a
         * freeze silently dropped.
         *
         * Checked against dashboard-api rather than inferred from the
         * generated types, which type `facility` optional but describe a shape,
         * not the server's rule: `createEpisodeFromEntry` requires only
         * `storedFrom`, and a patient actor with no directory references takes
         * the `unresolvedFacilityCapture` path, where the free text is retained
         * as `locator.legacy.facilityText` and is explicitly nullable. An
         * earlier attempt at this appeared to fail server-side; the cause was
         * the placement being sent twice, fixed separately, and not the missing
         * name.
         */
        const storageLocations =
          cycle.storageState === "stored" && dateString
            ? [baseStorage]
            : undefined;
        const storageStatus = storageLocations ? "stored" : "processed";

        /**
         * The placement for one row — see `placementForRow`.
         *
         * The reopened form is faithful now (`savedNotes` restores every field
         * it writes), so an update may carry the whole record again. What it
         * may not do is ask for a second episode, or leave an old one open when
         * she says the row is no longer stored.
         */
        const storageFieldsFor = (existingId: string | undefined) =>
          placementForRow(storageLocations, existingId, cycle.specimenRefs);

        /** See `surplusClosures` — a smaller count has to reach the rows it drops. */
        const closeSurplus = (
          type: "oocyte" | "semen" | "embryo",
          keptCount: number,
        ) => {
          for (const closure of surplusClosures(
            existingIds,
            keptCount,
            cycle.specimenRefs,
          )) {
            const identity = specimenItemIdentity(cycle, closure.index);
            // Once: without a stored key `getIdempotencyKey` mints a new one on
            // every call, and a retry that changed the client item key would
            // slip past the server's per-item idempotency as a fresh write.
            const idempotencyKey = getIdempotencyKey(identity);
            requests.push({
              cycleIndex,
              specimenIndex: closure.index,
              identity,
              existingId: closure.specimenId,
              retires: true,
              idempotencyKey,
              batchItem: {
                clientItemKey: idempotencyKey,
                operation: "update",
                type,
                specimenId: closure.specimenId,
                data: closure.data,
              },
            });
          }
        };

        // ── Oocytes ──
        if (cycle.type === "eggs") {
          const o = cycle.oocyte;
          const freezeUsage: Record<string, unknown> = {
            usage: "freeze",
            // Stated, and stated as false. Omitting it was meant to avoid
            // implying that the patient authorized the freeze — but
            // `has_decision_and_consent` is a required boolean on the API's
            // usage record, so leaving it out failed the whole `.strict()` batch
            // and no specimen of any type could be saved from this app at all.
            // `false` says what an intake record actually knows: no decision and
            // consent was recorded, and it asserts no authority the record does
            // not have. Checked against dashboard-api rather than assumed: the
            // batch writes usages through `handleUsages`, which has no consent
            // guard, so `false` is accepted for all seven — the two services that
            // throw on it are the lifecycle-event and material-operation routes,
            // which this never touches. `treatment-context` reads it as consent
            // pending, which is exactly what an intake record is.
            hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
            date: dateString,
            freezingInfo: {
              freezingProtocol:
                o.freezingMethod === "vitrification"
                  ? "vitrification"
                  : o.freezingMethod === "slow_freeze"
                    ? "slow_freezing"
                    : null,
            },
            /*
             * The placement is stated once, on the specimen — NOT here as well.
             *
             * Sending it in both places made the server materialise two storage
             * episodes for one specimen, and the second was refused:
             * `createStorageEpisode` blocks a further placement while an
             * unresolved one is open ("Material already has an unresolved
             * storage record"), which surfaced here as `Conflict`,
             * `retryable: false`, on every item of the batch. A patient's
             * free-text facility never resolves to a site entity, so her
             * episodes are always the unresolved kind and always collide —
             * which is why naming the clinic made no difference.
             *
             * The specimen level is the right one of the two: the read path
             * files a usage-linked episode under `specimenUsages[]` and
             * excludes it from the specimen's own `storageLocations`, and this
             * app reads only the latter. Keeping this one instead would have
             * left the vault blind to her frozen material. The server's own
             * test for a patient recording a frozen specimen sends the
             * placement at `data` level alone.
             */
          };

          for (let i = 0; i < o.numberFrozen; i++) {
            const identity = specimenItemIdentity(cycle, i);
            const idempotencyKey = getIdempotencyKey(identity);
            const body: Record<string, unknown> = {
              // Intake cannot create a typed discard/donation/use event. A
              // freeze record is either verified stored or remains pending.
              status: storageStatus,
              collectedAt,
              notes: notesStr || null,
              isDonor: donorFlag(cycle.source),
              ...storageFieldsFor(existingIds[i]),
              specimenUsages: [
                usageForRow(freezeUsage, existingIds[i], cycle.specimenRefs),
              ],
              ...(input.cycleId ? { cycleId: input.cycleId } : {}),
            };

            /*
             * Maturity is assigned by position, so it can only be assigned when
             * the mature count is known.
             *
             * An unknown `matureMII` used to collapse to 0, which shifted every
             * offset: the FIRST frozen rows were then written `mi` — a positive
             * mislabel on oocytes that are mature by definition, since an
             * immature one is never frozen. Leaving the stage unset says the
             * one true thing, that the record does not know.
             */
            const miiCount = o.matureMII;
            const miCount = o.immatureMI ?? 0;
            const degCount = o.degenerated ?? 0;
            if (miiCount != null) {
              if (i < miiCount) {
                body.maturityStage = "mii";
              } else if (i < miiCount + miCount) {
                body.maturityStage = "mi";
              } else if (i < miiCount + miCount + degCount) {
                body.maturityStage = "degenerated";
              }
            }

            if (o.numberThawed != null && i < o.numberThawed) {
              const thawUsage: Record<string, unknown> = {
                usage: "thaw",
                hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
              };
              if (o.postThawSurvivalRate != null) {
                thawUsage.thawingInfo = {
                  didSurvive: o.postThawSurvivalRate > 0,
                };
              }
              body.specimenUsages = [
                ...(body.specimenUsages as unknown[]),
                usageForRow(thawUsage, existingIds[i], cycle.specimenRefs),
              ];
            }

            /*
             * An existing row is updated, not added to. The form reopens
             * faithful to what it wrote — `savedNotes` restores every field —
             * so writing the whole record back changes only what she changed.
             */
            const existingId = existingIds[i];
            requests.push({
              cycleIndex,
              specimenIndex: i,
              identity,
              ...(existingId ? { existingId } : {}),
              idempotencyKey,
              batchItem: existingId
                ? {
                    clientItemKey: idempotencyKey,
                    operation: "update",
                    type: "oocyte",
                    specimenId: existingId,
                    data: body as UpdateOocyteBatchItem["data"],
                  }
                : {
                    clientItemKey: idempotencyKey,
                    operation: "create",
                    type: "oocyte",
                    data: body as CreateOocyteBatchItem["data"],
                  },
            });
          }

          closeSurplus("oocyte", o.numberFrozen);

          // ── Sperm (semen specimens) ──
        } else if (cycle.type === "sperm") {
          const s = cycle.sperm;
          const freezeUsage: Record<string, unknown> = {
            usage: "freeze",
            // See the eggs branch above for why this is stated.
            hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
            date: dateString,
            // Stated on the specimen only — see the eggs branch above for why
            // sending it here too made the server refuse the whole batch.
          };

          // Build spermiogram sub-object only if any analysis values present.
          // The API requires `patientId` on the spermiogram itself.
          const hasAnalysis =
            s.concentration != null ||
            s.totalCount != null ||
            s.motilityPercent != null ||
            s.morphologyPercent != null ||
            s.volumeMl != null ||
            s.progressiveMotilityPercent != null ||
            s.processingMethod !== "" ||
            s.otherDetails.trim() !== "";
          const spermiogram =
            hasAnalysis && patientId
              ? {
                  patientId,
                  ...(s.concentration != null
                    ? { spermConcentration: s.concentration }
                    : {}),
                  ...(s.totalCount != null ? { spermCount: s.totalCount } : {}),
                  ...(s.motilityPercent != null
                    ? { motility: s.motilityPercent }
                    : {}),
                  ...(s.progressiveMotilityPercent != null
                    ? { progressiveMotility: s.progressiveMotilityPercent }
                    : {}),
                  ...(s.morphologyPercent != null
                    ? { normalMorphology: s.morphologyPercent }
                    : {}),
                  ...(s.volumeMl != null ? { semenVolume: s.volumeMl } : {}),
                  ...(s.processingMethod
                    ? {
                        preparationMethod:
                          SEMEN_PREPARATION_METHODS[s.processingMethod] ?? null,
                      }
                    : {}),
                  // Her "Other" free text, kept on the analysis record itself.
                  // It used to live only inside the serialised notes, which
                  // the vault's detail sheet never reads for this field.
                  ...(s.otherDetails.trim()
                    ? { notes: s.otherDetails.trim() }
                    : {}),
                }
              : null;

          for (let i = 0; i < s.numberFrozen; i++) {
            const identity = specimenItemIdentity(cycle, i);
            const idempotencyKey = getIdempotencyKey(identity);
            const body: Record<string, unknown> = {
              status: storageStatus,
              collectedAt,
              notes: notesStr || null,
              isDonor: donorFlag(cycle.source),
              ...storageFieldsFor(existingIds[i]),
              specimenUsages: [
                usageForRow(freezeUsage, existingIds[i], cycle.specimenRefs),
              ],
              // The legacy API field describes the stored state. The
              // patient-facing preparation choice is sent in spermiogram and
              // included in notesStr above.
              processingMethod: "frozen",
              // Omitted, never nulled: the field is a non-nullable enum on
              // every variant of this endpoint, so `null` fails validation
              // exactly as an unmapped string would — and because the batch
              // checks each item `.strict()`, that one field took the whole
              // save down with it. Omitting also stops an update from wiping a
              // value the clinic supplied. "Other" has no enum member, so on
              // *create* it cannot save until the backend lets the column be
              // absent; the patient's answer survives in `notesStr` either way.
              ...(s.collectionMethod &&
              SEMEN_COLLECTION_METHODS_TO_API[s.collectionMethod]
                ? {
                    collectionMethod:
                      SEMEN_COLLECTION_METHODS_TO_API[s.collectionMethod],
                  }
                : {}),
              ...(sampleIds[semenSampleIdentity(cycle)]
                ? { sampleId: sampleIds[semenSampleIdentity(cycle)] }
                : {}),
              ...(s.abstinencePeriod != null
                ? { abstinencePeriod: s.abstinencePeriod }
                : {}),
              ...(s.origin !== "unknown" ? { origin: s.origin } : {}),
              // Vitality and DNA fragmentation are specimen-level metrics on
              // the API (canonical observations), not spermiogram columns. They
              // were serialised into `notes` and nowhere else, so the vault —
              // which reads structured fields — showed them as "Not tested"
              // however many times she typed them in (#1964).
              // The API bounds both to 0–100 and the batch is validated
              // item by item, so an out-of-range figure is left to the notes
              // (where it always went) rather than failing her whole save.
              ...(isPercent(s.vitalityPercent)
                ? { vitality: s.vitalityPercent }
                : {}),
              ...(isPercent(s.dnaFragmentationPercent)
                ? { dnaFragmentation: s.dnaFragmentationPercent }
                : {}),
              ...(spermiogram ? { spermiogram } : {}),
              ...(input.cycleId ? { cycleId: input.cycleId } : {}),
            };

            /*
             * An existing row is updated, not added to. The form reopens
             * faithful to what it wrote — `savedNotes` restores every field —
             * so writing the whole record back changes only what she changed.
             */
            const existingId = existingIds[i];
            requests.push({
              cycleIndex,
              specimenIndex: i,
              identity,
              ...(existingId ? { existingId } : {}),
              idempotencyKey,
              batchItem: existingId
                ? {
                    clientItemKey: idempotencyKey,
                    operation: "update",
                    type: "semen",
                    specimenId: existingId,
                    data: body as UpdateSemenBatchItem["data"],
                  }
                : {
                    clientItemKey: idempotencyKey,
                    operation: "create",
                    type: "semen",
                    data: body as CreateSemenBatchItem["data"],
                  },
            });
          }

          closeSurplus("semen", s.numberFrozen);

          // ── Embryos (individual mode) ──
        } else if (cycle.embryoEntryMode === "individual") {
          const em = cycle.embryo;

          for (let i = 0; i < em.individualEntries.length; i++) {
            const identity = specimenItemIdentity(cycle, i);
            const idempotencyKey = getIdempotencyKey(identity);
            const entry = em.individualEntries[i]!;
            const entryDay = entry.dayOfDevelopment || em.dayOfDevelopment;
            const dayDetails = parseGrade(entry.grade, entryDay);
            const isFrozenEntry =
              entry.status === "frozen" || entry.status === "thawed_refrozen";

            const freezeUsage: Record<string, unknown> = {
              usage: "freeze",
              // See the eggs branch above for why this is stated.
              hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
              date: dateString,
              // Stated on the specimen only — see the eggs branch above for why
              // sending it here too made the server refuse the whole batch.
            };

            const biopsyData = buildEmbryoBiopsy(
              em.pgtPerformed,
              entryDay,
              entry.geneticStatus,
            );
            const usages: Record<string, unknown>[] = isFrozenEntry
              ? [freezeUsage]
              : [];
            if (biopsyData) {
              usages.push({
                usage: "biopsy",
                hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
                embryoBiopsy: biopsyData,
              });
            }

            const body: Record<string, unknown> = {
              status: isFrozenEntry ? storageStatus : "processed",
              collectedAt,
              notes: entry.notes || notesStr || null,
              identificationNumber: entry.embryoId.trim() || null,
              isDonor: donorFlag(cycle.source),
              /*
               * An entry that is no longer frozen still has to END its
               * placement, not merely say `processed`: the vault needs both,
               * and an episode left open is read straight back as "stored" on
               * the next reopen, contradicting what she just saved.
               */
              ...(isFrozenEntry
                ? storageFieldsFor(existingIds[i])
                : placementForRow(
                    undefined,
                    existingIds[i],
                    cycle.specimenRefs,
                  )),
              ...(Object.keys(dayDetails).length > 0
                ? { dayDetails: [dayDetails] }
                : {}),
              specimenUsages: usages.map((usage) =>
                usageForRow(usage, existingIds[i], cycle.specimenRefs),
              ),
              ...(input.cycleId ? { cycleId: input.cycleId } : {}),
            };

            /*
             * An existing row is updated, not added to. The form reopens
             * faithful to what it wrote — `savedNotes` restores every field —
             * so writing the whole record back changes only what she changed.
             */
            const existingId = existingIds[i];
            requests.push({
              cycleIndex,
              specimenIndex: i,
              identity,
              ...(existingId ? { existingId } : {}),
              idempotencyKey,
              batchItem: existingId
                ? {
                    clientItemKey: idempotencyKey,
                    operation: "update",
                    type: "embryo",
                    specimenId: existingId,
                    data: body as UpdateEmbryoBatchItem["data"],
                  }
                : {
                    clientItemKey: idempotencyKey,
                    operation: "create",
                    type: "embryo",
                    data: body as CreateEmbryoBatchItem["data"],
                  },
            });
          }

          closeSurplus("embryo", em.individualEntries.length);

          // ── Embryos (group mode) ──
        } else {
          const em = cycle.embryo;
          const dayNum = parseInt(em.dayOfDevelopment, 10);
          const dayDetails = Number.isNaN(dayNum) ? null : { day: dayNum };

          const freezeUsage: Record<string, unknown> = {
            usage: "freeze",
            // See the eggs branch above for why this is stated.
            hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
            date: dateString,
            // Stated on the specimen only — see the eggs branch above for why
            // sending it here too made the server refuse the whole batch.
          };

          const biopsyData = buildEmbryoBiopsy(
            em.pgtPerformed,
            em.dayOfDevelopment,
          );
          const usages: Record<string, unknown>[] = [freezeUsage];
          if (biopsyData) {
            usages.push({
              usage: "biopsy",
              hasDecisionAndConsent: SPECIMEN_INTAKE_HAS_CONSENT,
              embryoBiopsy: biopsyData,
            });
          }

          for (let i = 0; i < em.numberFrozen; i++) {
            const identity = specimenItemIdentity(cycle, i);
            const idempotencyKey = getIdempotencyKey(identity);
            const body: Record<string, unknown> = {
              status: storageStatus,
              collectedAt,
              notes: notesStr || null,
              isDonor: donorFlag(cycle.source),
              ...storageFieldsFor(existingIds[i]),
              ...(dayDetails ? { dayDetails: [dayDetails] } : {}),
              specimenUsages: usages.map((usage) =>
                usageForRow(usage, existingIds[i], cycle.specimenRefs),
              ),
              ...(input.cycleId ? { cycleId: input.cycleId } : {}),
            };

            /*
             * An existing row is updated, not added to. The form reopens
             * faithful to what it wrote — `savedNotes` restores every field —
             * so writing the whole record back changes only what she changed.
             */
            const existingId = existingIds[i];
            requests.push({
              cycleIndex,
              specimenIndex: i,
              identity,
              ...(existingId ? { existingId } : {}),
              idempotencyKey,
              batchItem: existingId
                ? {
                    clientItemKey: idempotencyKey,
                    operation: "update",
                    type: "embryo",
                    specimenId: existingId,
                    data: body as UpdateEmbryoBatchItem["data"],
                  }
                : {
                    clientItemKey: idempotencyKey,
                    operation: "create",
                    type: "embryo",
                    data: body as CreateEmbryoBatchItem["data"],
                  },
            });
          }

          closeSurplus("embryo", em.numberFrozen);
        }
      }

      const retryIdempotencyKeys = Object.fromEntries(
        requests.map((request) => [request.identity, request.idempotencyKey]),
      );
      const retrySampleIds = Object.fromEntries(
        input.cycles
          .map((cycle) =>
            cycle.type === "sperm" && sampleIds[semenSampleIdentity(cycle)]
              ? [
                  semenSampleIdentity(cycle),
                  sampleIds[semenSampleIdentity(cycle)],
                ]
              : null,
          )
          .filter((entry): entry is [string, string] => entry !== null),
      );

      const batchResult = await (async () => {
        try {
          return await client.POST("/patients/{patientId}/specimens/batch", {
            params: {
              header: { "Idempotency-Key": operationKey },
              path: { patientId },
            },
            body: { items: requests.map((request) => request.batchItem) },
          });
        } catch (error) {
          await queryClient.invalidateQueries({ queryKey: ["specimens"] });
          await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
          // Redacted (#2247).
          logClinicalMutationError(
            "[useCreateSpecimen]",
            "/{tenantId}/patients/{patientId}/specimens/batch",
            error,
          );
          throw new SpecimenBatchError(
            input.cycles,
            0,
            requests.length,
            retryIdempotencyKeys,
            retrySampleIds,
            operationKey,
          );
        }
      })();

      const responseData = batchResult.data?.data;

      if (batchResult.error || !responseData) {
        await queryClient.invalidateQueries({ queryKey: ["specimens"] });
        await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
        // Redacted: batch error bodies echo rejected clinical fields (#2247).
        logClinicalMutationError(
          "[useCreateSpecimen]",
          "/{tenantId}/patients/{patientId}/specimens/batch",
          batchResult.error,
        );
        throw new SpecimenBatchError(
          input.cycles,
          0,
          requests.length,
          retryIdempotencyKeys,
          retrySampleIds,
          operationKey,
        );
      }

      const failed = responseData.items.filter(
        (item) => item.status === "failed",
      );
      if (failed.length > 0) {
        await queryClient.invalidateQueries({ queryKey: ["specimens"] });
        await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
        for (const item of failed) {
          /*
           * The operation and type as well as the code. `internal_error` alone
           * says only "not an `ApiError`" — a raw throw — and the first thing
           * worth knowing is whether the item was a create or an update,
           * because it is the update path that carries ids the server has to
           * resolve. Without it, a batch that failed on every item took a read
           * of the server's own source to explain.
           */
          console.error(
            "[useCreateSpecimen] Batch item failed:",
            item.clientItemKey,
            item.operation,
            item.type,
            item.errorCode,
            item.retryable ? "retryable" : "not retryable",
          );
        }
        throw new SpecimenBatchError(
          input.cycles,
          responseData.items.length - failed.length,
          failed.length,
          retryIdempotencyKeys,
          retrySampleIds,
          operationKey,
        );
      }

      const specimenIdsByCycle: Record<number, string[]> = {};
      const retiredSpecimenIdsByCycle: Record<number, string[]> = {};
      for (const request of requests) {
        const item = responseData.items.find(
          (candidate) => candidate.clientItemKey === request.idempotencyKey,
        );
        if (!item) continue;
        if (request.retires) {
          /*
           * From the request, not the response: `specimenId` is nullable on a
           * response item, and a retirement that came back without one would
           * stay in the cycle's identity and be deleted a second time — the
           * failure this list exists to prevent. The client already knows which
           * row it retired.
           */
          if (!request.existingId) continue;
          retiredSpecimenIdsByCycle[request.cycleIndex] = [
            ...(retiredSpecimenIdsByCycle[request.cycleIndex] ?? []),
            request.existingId,
          ];
          continue;
        }
        if (!item.specimenId) continue;
        specimenIdsByCycle[request.cycleIndex] = [
          ...(specimenIdsByCycle[request.cycleIndex] ?? []),
          item.specimenId,
        ];
      }

      return {
        ...responseData,
        specimenIdsByCycle,
        retiredSpecimenIdsByCycle,
      } satisfies SpecimenBatchResult;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["specimens"] });
      await queryClient.invalidateQueries({ queryKey: ["oocyte-cohorts"] });
    },
  });
}
